import { useRef } from "react";
import { useDispatch } from "react-redux";

import { FeatureCollection, Geometry } from "geojson";

import { useFetchAdminAreasByIds } from "@/api/adminArea/userAdminAreaAPI";
import { optimizeSelectValue } from "@/api/configuration/useConfigurationAPI";
import { useFetchBulkIsochrone } from "@/api/isochrone/useIsochroneAPI";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import {
  initialCatchmentSetting,
  setAdminAreaAddress,
  setAdminAreaSettingsData,
  setAdminTreeSettingsSelectedIds,
  setAreaInput,
  setCatchmentSettings,
  setOverrideGlobalFilter
} from "@/store/slice/spatialSlice";
import type {
  AdministrativeArea,
  AdministrativeAreaExtract
} from "@/types/adminArea";
import type { MarkerPinItem } from "@/types/map";
import {
  AreaInputType,
  type CatchmentSettings,
  type CatchmentTransport,
  ECatchmentInputBy,
  ECatchmentTransport,
  ECatchmentType,
  ERoadAccessMeasurement,
  ERoadExclusions,
  type IsochroneData,
  type RoadAccessMeasurement
} from "@/types/spatial";
import { getEffectiveRoadExclusions } from "@/utils/catchment";
import { getRadiusPolygon } from "@/utils/map";

import type {
  AnalysisConfiguration,
  AreaInput,
  Catchment
} from "@lokasi-ai-web-sdk/agent-ui";
import { featureCollection, point } from "@turf/turf";

import { hasBoundary } from "./utils";

/** `profile: "radius"` is the one profile that is not a travel mode. */
const RADIUS_PROFILE = "radius";

/** The travel modes the isochrone endpoint routes for. */
const ROUTING_TYPES: CatchmentTransport[] = [
  ECatchmentTransport.WALKING,
  ECatchmentTransport.CYCLING,
  ECatchmentTransport.DRIVING
];

/**
 * The SDK leaves `profile` open, so a mode this app cannot route resolves to
 * nothing rather than to a request the backend would reject — the pins still
 * land, the shape simply never arrives.
 */
const toRoutingType = (profile: string): CatchmentTransport | undefined =>
  ROUTING_TYPES.find((routingType) => routingType === profile);

/**
 * What the ledger compares: the definition, never a shape derived from it. The
 * contract keeps geometry out of state, so two syncs carrying the same profile,
 * metric, contours and locations describe the same area even though the
 * isochrone behind it was fetched separately each time.
 */
const getAreaInputSignature = (areaInput: AreaInput) =>
  JSON.stringify({
    admAreaIds: areaInput.adm_area_ids ?? null,
    isGlobal: areaInput.is_global_admin_area ?? false,
    catchment: areaInput.catchment
      ? {
          profile: areaInput.catchment.profile,
          metric: areaInput.catchment.metric,
          contours: areaInput.catchment.contours,
          exclusions: areaInput.catchment.exclusions ?? [],
          locations: areaInput.catchment.locations.map((location) => [
            location.name,
            location.longitude,
            location.latitude
          ])
        }
      : null
  });

/**
 * The pins, straight from the definition. Ids are derived from the position so
 * an unchanged definition produces unchanged ids — `useMapMarkers` keys its DOM
 * markers off them and would otherwise churn every one on every delta.
 */
const toLocationPins = (catchment: Catchment): MarkerPinItem[] =>
  catchment.locations.map((location, index) => ({
    lng: location.longitude,
    lat: location.latitude,
    locationName: location.name,
    id: `agent-location-${index}`
  }));

/**
 * The catchment as the `spatial` slice models it. Contours travel as numbers
 * and are stored as strings, because that is the edit model the catchment popup
 * writes and every reader already expects.
 */
const toCatchmentSettings = (
  catchment: Catchment,
  isochroneData?: IsochroneData[]
): CatchmentSettings => {
  const locationPins = toLocationPins(catchment);
  const contours = catchment.contours.map(String);
  const isRadiusProfile = catchment.profile === RADIUS_PROFILE;
  const isDistanceMetric = catchment.metric === ERoadAccessMeasurement.DISTANCE;

  return {
    // `setCatchmentSettings` merges, so spreading the initial settings is what
    // clears a previous definition — e.g. a stale `radiusData` when the agent
    // switches to a travel profile.
    ...initialCatchmentSetting,
    catchmentInputBy: ECatchmentInputBy.BY_LOCATION,
    catchmentType: isRadiusProfile
      ? ECatchmentType.BY_RADIUS
      : ECatchmentType.BY_ROAD,
    placeCoordinates: locationPins,
    // A radius profile is always metres, whatever `metric` says.
    radiusData: isRadiusProfile ? contours : [],
    roadAccessConfig: isRadiusProfile
      ? initialCatchmentSetting.roadAccessConfig
      : {
          routingType: toRoutingType(catchment.profile),
          measurementType: catchment.metric as RoadAccessMeasurement,
          roadExclusions: (catchment.exclusions ?? []) as ERoadExclusions[],
          distances: isDistanceMetric ? contours : [],
          durations: isDistanceMetric ? [] : contours,
          isochroneData
        }
  };
};

/** Everything drawable the definition produced, for the camera to frame. */
const toFrameableFeatures = (
  catchment: Catchment,
  isochroneData: IsochroneData[] | undefined
): FeatureCollection<Geometry> => {
  const isochroneFeatures = (isochroneData ?? [])
    .flatMap((item) => item.data ?? [])
    .flatMap((collection) => collection.features ?? []);

  if (isochroneFeatures.length > 0) {
    return featureCollection(isochroneFeatures);
  }

  const locationPins = toLocationPins(catchment);

  if (catchment.profile === RADIUS_PROFILE) {
    const radii = catchment.contours.filter((contour) => contour > 0);

    return featureCollection(
      locationPins.flatMap((pin) =>
        radii.map((radius) => getRadiusPolygon([pin.lng, pin.lat], radius))
      )
    );
  }

  // A travel profile whose shape never arrived still has its pins.
  return featureCollection(
    locationPins.map((pin) => point([pin.lng, pin.lat]))
  );
};

/**
 * Draws the analysis area the agent settled on — the catchment or the
 * administrative selection under `area_input` — and frames the map on it.
 *
 * Nothing here paints: it resolves the definition into the shape the `spatial`
 * slice models, and `useCatchmentArea`, `useMapMarkers` and `useAdminArea`
 * repaint off the store on their own, exactly as they do for a user editing the
 * catchment popup. A travel profile's polygon is never in state, so it is
 * re-requested from the definition every time it is new — including on a cold
 * load, where the agent's own isochrone preview event is long gone.
 *
 * It tracks the one area it applied, because the `spatial` slice is shared with
 * Spatial Analysis and restored from storage: a workspace that never had an
 * area must leave the user's own selection alone.
 */
export const useAgentAreaInput = () => {
  const dispatch = useDispatch();
  const { isNewRoadExclusionEnabled } = useAppFeatures();
  const { handleMovePositionToLayer, fitMapToLayerBound } = useMap();

  const fetchAdminAreasByIds = useFetchAdminAreasByIds();
  const fetchBulkIsochrone = useFetchBulkIsochrone();

  /**
   * The definition currently dispatched to the store, and whether its travel
   * shape arrived with it — an unshaped one is re-attempted on the next sync.
   */
  const appliedAreaRef = useRef<{
    signature: string;
    hasShape: boolean;
  } | null>(null);

  const clearAppliedArea = () => {
    dispatch(setAreaInput(null));
    dispatch(setCatchmentSettings(initialCatchmentSetting));
    dispatch(setAdminAreaSettingsData(null));
    dispatch(setAdminTreeSettingsSelectedIds([]));
    appliedAreaRef.current = null;
  };

  const applyAdminAreaMode = (areaInput: AreaInput) => {
    dispatch(
      setAreaInput({
        label: "Administrative Area",
        value: AreaInputType.ADMIN_AREA
      })
    );
    // Leaving a catchment behind would keep its circles and pins on the map.
    dispatch(setCatchmentSettings(initialCatchmentSetting));
    dispatch(setOverrideGlobalFilter(!areaInput.is_global_admin_area));
  };

  const fetchIsochrone = async (catchment: Catchment) => {
    const routingType = toRoutingType(catchment.profile);
    const coordinates = catchment.locations.map(
      (location) => `${location.longitude},${location.latitude}`
    );

    if (
      !routingType ||
      coordinates.length === 0 ||
      !catchment.contours.length
    ) {
      return;
    }

    return fetchBulkIsochrone({
      profile: routingType,
      contour_metric: catchment.metric as ERoadAccessMeasurement,
      contours: [...catchment.contours],
      // Filtered the way the catchment popup filters them, so the agent and the
      // user send the backend the same request.
      excludes: getEffectiveRoadExclusions(
        (catchment.exclusions ?? []) as ERoadExclusions[],
        routingType,
        isNewRoadExclusionEnabled
      ),
      coordinates
    });
  };

  const syncCatchment = async (
    areaInput: AreaInput,
    catchment: Catchment,
    signature: string,
    signal: AbortSignal
  ) => {
    const isRadiusProfile = catchment.profile === RADIUS_PROFILE;

    let isochroneData: IsochroneData[] | undefined;
    let hasShape = true;

    if (!isRadiusProfile) {
      try {
        isochroneData = await fetchIsochrone(catchment);
      } catch (error) {
        // The pins are still worth drawing, so this dispatches without a shape
        // and leaves the ledger unshaped for the next sync to retry.
        console.warn("Agent analysis area could not be routed", { error });
        hasShape = false;
      }

      if (signal.aborted) return;
    }

    // Nothing new to say: this pass only re-attempted a shape it still could
    // not get, and the definition is already on the map.
    if (!hasShape && appliedAreaRef.current?.signature === signature) return;

    dispatch(
      setAreaInput({ label: "Catchment", value: AreaInputType.CATCHMENT })
    );
    dispatch(
      setCatchmentSettings(toCatchmentSettings(catchment, isochroneData))
    );
    dispatch(setOverrideGlobalFilter(!areaInput.is_global_admin_area));

    handleMovePositionToLayer(toFrameableFeatures(catchment, isochroneData));

    appliedAreaRef.current = { signature, hasShape };
  };

  const syncAdmAreas = async (
    areaInput: AreaInput,
    admAreaIds: readonly string[],
    signature: string,
    signal: AbortSignal
  ) => {
    const resolvedAreas = await fetchAdminAreasByIds([...admAreaIds]);

    if (signal.aborted) return;

    const drawableAreas = resolvedAreas.filter(
      (area): area is AdministrativeArea => hasBoundary(area)
    );

    if (drawableAreas.length === 0) {
      console.warn("Agent analysis area has no boundary to draw", {
        admAreaIds
      });
      return;
    }

    applyAdminAreaMode(areaInput);
    // Without the admin tree, `useAdminArea` re-derives the geo data from this
    // address — left unset it resolves to null and wipes what lands below.
    dispatch(
      setAdminAreaAddress(
        optimizeSelectValue(drawableAreas[0] as AdministrativeAreaExtract)
      )
    );
    dispatch(setAdminAreaSettingsData({ data: drawableAreas }));
    dispatch(setAdminTreeSettingsSelectedIds([...admAreaIds]));

    fitMapToLayerBound({ data: drawableAreas });

    appliedAreaRef.current = { signature, hasShape: true };
  };

  const syncAreaInput = async (
    configuration: AnalysisConfiguration | null,
    signal: AbortSignal
  ) => {
    const areaInput = configuration?.area_input ?? null;

    if (!areaInput) {
      // Only reachable with something in the ledger, so this takes back an area
      // this hook put there and never a selection the user made.
      if (appliedAreaRef.current) clearAppliedArea();
      return;
    }

    const signature = getAreaInputSignature(areaInput);
    const appliedArea = appliedAreaRef.current;

    // Already on the map, shape and all. Returning here keeps every delta of a
    // run from re-routing and yanking the camera away from a user who is
    // panning.
    if (appliedArea?.signature === signature && appliedArea.hasShape) return;

    if (areaInput.catchment?.locations?.length) {
      await syncCatchment(areaInput, areaInput.catchment, signature, signal);
      return;
    }

    if (areaInput.adm_area_ids?.length) {
      await syncAdmAreas(areaInput, areaInput.adm_area_ids, signature, signal);
      return;
    }

    // No area of its own: the analysis runs over the working area
    // `useAgentGlobalAdmArea` drew, so the mode is administrative and the camera
    // stays where that left it.
    if (areaInput.is_global_admin_area) {
      applyAdminAreaMode(areaInput);
      appliedAreaRef.current = { signature, hasShape: true };
    }
  };

  return { syncAreaInput, clearAppliedArea };
};
